import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Trophy, RotateCcw, X, Ticket, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Performer } from "@shared/schema";

interface RaffleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  performers: Performer[];
  jamNightId: number;
}

interface RaffleResult {
  winnerId: number;
  winnerName: string;
  totalEntrances: number;
  eligibleParticipants: number;
}

export default function RaffleModal({
  open,
  onOpenChange,
  performers,
  jamNightId,
}: RaffleModalProps) {
  const [raffleResult, setRaffleResult] = useState<RaffleResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const runRaffleMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/events/${jamNightId}/raffle`, {});
      return response.json();
    },
    onSuccess: (data) => {
      setRaffleResult({
        winnerId: data.winner.id,
        winnerName: data.winner.name,
        totalEntrances: data.stats.totalEntrances,
        eligibleParticipants: data.stats.eligibleParticipants,
      });
      
      setIsRunning(false);
      
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      
      toast({
        title: "🎉 Raffle Winner!",
        description: `${data.winner.name} has won the raffle!`,
      });
    },
    onError: (error: any) => {
      setIsRunning(false);
      
      toast({
        title: "Error",
        description: error.message || "Failed to run raffle",
        variant: "destructive",
      });
    },
  });

  const resetRaffleDataMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/events/${jamNightId}/raffle/reset`, {});
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      
      toast({
        title: "Raffle Data Reset",
        description: `Reset raffle data for ${data.affectedPerformers} participants`,
      });
      
      setResetConfirmOpen(false);
      setRaffleResult(null); // Clear any existing raffle result
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reset raffle data",
        variant: "destructive",
      });
      setResetConfirmOpen(false);
    },
  });

  // Calculate eligible participants and their effective entrances
  const getEligibleParticipants = () => {
    return performers.filter(performer => {
      const effectiveEntrances = (performer.raffleEntrances || 0) - (performer.raffleWins || 0);
      return !performer.removed && effectiveEntrances > 0;
    }).map(performer => ({
      ...performer,
      effectiveEntrances: (performer.raffleEntrances || 0) - (performer.raffleWins || 0)
    }));
  };

  const runRaffle = async () => {
    const eligibleParticipants = getEligibleParticipants();
    
    if (eligibleParticipants.length === 0) {
      toast({
        title: "No eligible participants",
        description: "No participants have raffle entrances available.",
        variant: "destructive",
      });
      return;
    }

    setIsRunning(true);
    
    // Add a visual delay for suspense
    setTimeout(() => {
      runRaffleMutation.mutate();
    }, 2000); // 2 second delay for suspense
  };

  const resetRaffle = () => {
    setRaffleResult(null);
  };

  const closeModal = () => {
    setRaffleResult(null);
    onOpenChange(false);
  };

  const eligibleParticipants = getEligibleParticipants();
  const totalEntrances = eligibleParticipants.reduce((sum, p) => sum + p.effectiveEntrances, 0);

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Event Raffle
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {!raffleResult ? (
            <>
              {/* Raffle Statistics */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{eligibleParticipants.length}</div>
                  <div className="text-sm text-slate-600">Eligible Participants</div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-semibold text-green-600">{totalEntrances}</div>
                  <div className="text-sm text-slate-600">Total Raffle Entries</div>
                </div>
              </div>

              {/* Eligible Participants List */}
              {eligibleParticipants.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Participants & Entries:</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {eligibleParticipants.map((participant) => (
                      <div key={participant.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <span className="text-sm font-medium">{participant.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            <Ticket className="h-3 w-3 mr-1" />
                            {participant.effectiveEntrances} entries
                          </Badge>
                          {(participant.raffleWins || 0) > 0 && (
                            <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                              <Trophy className="h-3 w-3 mr-1" />
                              {participant.raffleWins} wins
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Reset Raffle Data Button */}
                {(eligibleParticipants.length > 0 || performers.some(p => (p.raffleEntrances || 0) > 0 || (p.raffleWins || 0) > 0)) && (
                  <Button
                    onClick={() => setResetConfirmOpen(true)}
                    disabled={isRunning || resetRaffleDataMutation.isPending}
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset All Raffle Data
                  </Button>
                )}
                
                {/* Run Raffle Button */}
                <Button
                  onClick={runRaffle}
                  disabled={isRunning || eligibleParticipants.length === 0}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium py-3"
                >
                  {isRunning ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Drawing Winner...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Run Raffle ({totalEntrances} entries)
                    </div>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Winner Display */}
              <div className="text-center space-y-6">
                <div className="animate-bounce">
                  <Trophy className="h-16 w-16 text-yellow-500 mx-auto" />
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">🎉 Winner! 🎉</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-4">
                    {raffleResult.winnerName}
                  </div>
                  <div className="text-sm text-slate-600">
                    Selected from {raffleResult.eligibleParticipants} participants with {raffleResult.totalEntrances} total entries
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={resetRaffle}
                    variant="outline"
                    className="flex-1"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Run Another Raffle
                  </Button>
                  <Button
                    onClick={closeModal}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
      
      {/* Reset Confirmation Dialog */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset All Raffle Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently reset all raffle entrances and wins for all participants in this event to zero. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetRaffleDataMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetRaffleDataMutation.mutate()}
              disabled={resetRaffleDataMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resetRaffleDataMutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Resetting...
                </div>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Reset All Data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}